// src/pages/MeinTeam.jsx
//
// Oeffentliche Ansicht EINES Teams, erreichbar ueber /team/:token.
//
// Zweck: waehrend der Veranstaltung soll jedes Team seinen eigenen Stand am
// Handy abrufen koennen (spaeter per QR-Code am Stand), ohne Anmeldung und ohne
// dass damit die Gesamtwertung vorab oeffentlich wird.
//
// Die Seite zeigt bewusst KEINEN Rang. Die Platzierung wird bei der
// Preisverleihung enthuellt – das Backend liefert sie deshalb gar nicht erst
// mit (siehe functions/routes/public.js).
//
// Solange nur `live_team_view` an ist (also waehrend der Veranstaltung), zeigt
// die Seite ausserdem NUR die eigenen Rohergebnisse – keine Stationspunkte,
// keine Gesamtpunkte, keinen Altersbonus. Diese Werte entstehen erst aus dem
// Vergleich mit allen anderen Teams und wuerden verraten, wo das Team im Feld
// steht. Das Backend liefert sie vor der Freigabe gar nicht mit; `daten.
// results_published` sagt hier, welcher der beiden Zustaende gilt.
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Trophy, RefreshCw, ChevronDown, CheckCircle2, Circle, MapPin } from "lucide-react";
import Badge from "../components/ui/Badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Waehrend der Veranstaltung aendern sich die Ergebnisse laufend. 45 s ist ein
// Kompromiss: haeufig genug, dass es sich "live" anfuehlt, selten genug, dass
// 40+ dauerhaft geoeffnete Handys die Function nicht unnoetig aufwecken.
const POLL_MS = 45000;

const typeColors = {
  Männer: "badge-team-men",
  Frauen: "badge-team-women",
  Mixed: "badge-team-mixed",
};

export default function MeinTeam() {
  const { token } = useParams();

  const [daten, setDaten] = useState(null);
  const [ladend, setLadend] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [aktualisiert, setAktualisiert] = useState(null);
  // Station, die nach einem Tipp auf ihren Lageplan-Pin kurz hervorgehoben wird.
  const [markiert, setMarkiert] = useState(null);
  const markierTimer = useRef(null);
  // Aufgeklappte Spiele der Liste – der Lageplan hebt deren Pins hervor.
  const [aufgeklappt, setAufgeklappt] = useState(() => new Set());
  const beiAufklappen = useCallback((nummer, offen) => {
    setAufgeklappt((bisher) => {
      if (bisher.has(nummer) === offen) return bisher;
      const neu = new Set(bisher);
      if (offen) neu.add(nummer);
      else neu.delete(nummer);
      return neu;
    });
  }, []);

  // Verhindert, dass eine Antwort nach dem Unmount noch State setzt.
  const aktivRef = useRef(true);

  const laden = useCallback(
    async ({ still = false } = {}) => {
      if (!still) setLadend(true);
      try {
        const res = await fetch(`${BACKEND_URL}/public/team/${encodeURIComponent(token)}`);

        if (!aktivRef.current) return;

        if (res.status === 403) {
          setFehler("gesperrt");
          setDaten(null);
          return;
        }
        if (res.status === 404) {
          setFehler("unbekannt");
          setDaten(null);
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const body = await res.json();
        if (!aktivRef.current) return;

        setDaten(body);
        setFehler(null);
        setAktualisiert(new Date().toLocaleTimeString("de-DE"));
      } catch (err) {
        console.error("[MeinTeam] Fehler beim Laden:", err);
        // Ein Netzwerkfehler beim Hintergrund-Poll darf die bereits angezeigten
        // Ergebnisse nicht wegraeumen – auf dem Festgelaende ist das Netz
        // unzuverlaessig, und ein leerer Bildschirm waere schlechter als ein
        // leicht veralteter Stand.
        if (aktivRef.current && !daten) setFehler("netzwerk");
      } finally {
        if (aktivRef.current) setLadend(false);
      }
    },
    // `daten` bewusst als Abhaengigkeit: der Fehlerpfad oben unterscheidet
    // danach, ob schon etwas angezeigt wird.
    [token, daten]
  );

  useEffect(() => {
    aktivRef.current = true;
    laden();

    const timer = setInterval(() => laden({ still: true }), POLL_MS);

    // Beim Zurueckwechseln auf den Tab sofort nachladen, statt bis zum naechsten
    // Intervall einen veralteten Stand zu zeigen.
    const beiSichtbarkeit = () => {
      if (document.visibilityState === "visible") laden({ still: true });
    };
    document.addEventListener("visibilitychange", beiSichtbarkeit);

    return () => {
      aktivRef.current = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", beiSichtbarkeit);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => () => clearTimeout(markierTimer.current), []);

  // Pin angetippt: zur Zeile der Station scrollen und sie kurz markieren.
  const zuStation = (nummer) => {
    const zeile = document.getElementById(`station-${nummer}-zeile`);
    if (!zeile) return;
    zeile.scrollIntoView({ behavior: "smooth", block: "center" });
    setMarkiert(nummer);
    clearTimeout(markierTimer.current);
    markierTimer.current = setTimeout(() => setMarkiert(null), 2000);
  };

  // === Fehler- und Wartezustaende ===

  if (ladend && !daten) {
    return (
      <div className="p-6 text-center text-slate-300 opacity-80">
        Ergebnisse werden geladen …
      </div>
    );
  }

  if (fehler === "gesperrt") {
    return (
      <Hinweis farbe="yellow">
        Die Live-Ergebnisse sind derzeit nicht freigeschaltet. Probiere es später
        noch einmal.
      </Hinweis>
    );
  }

  if (fehler === "unbekannt") {
    return (
      <Hinweis farbe="red">
        Dieser Link gehört zu keinem Team. Bitte prüfe den QR-Code oder wende
        dich an die Turnierleitung.
      </Hinweis>
    );
  }

  if (fehler === "netzwerk") {
    return (
      <Hinweis farbe="red">
        Die Ergebnisse konnten nicht geladen werden. Bitte prüfe deine
        Internetverbindung.
      </Hinweis>
    );
  }

  if (!daten) return null;

  const team = daten.team || {};
  const stationen = daten.stations || [];
  const gesamt = daten.stations_total || stationen.length;
  const erledigt = stationen.filter((s) => s.erledigt).length;
  const prozent = gesamt > 0 ? Math.round((erledigt / gesamt) * 100) : 0;
  // Einzige Fallunterscheidung der Ansicht: vor der Freigabe Rohwerte,
  // danach zusaetzlich die Wertung.
  const mitWertung = !!daten.results_published;

  // === Ansicht ===

  return (
    <div className="max-w-xl mx-auto">
      {/* Kopf */}
      <div className="bg-secondary-900 border border-secondary-800 rounded-lg p-5 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-white break-words">
              {team.team_name}
            </h1>
            <div className="flex flex-wrap gap-1 mt-2">
              {team.event_type && (
                <Badge className="badge badge-secondary">{team.event_type}</Badge>
              )}
              {team.team_type && (
                <Badge className={`badge ${typeColors[team.team_type] ?? "badge-secondary"}`}>
                  {team.team_type}
                </Badge>
              )}
            </div>
          </div>

          {/* Vor der Freigabe steht hier der Fortschritt statt der Punkte –
              die grosse Zahl bleibt so besetzt und die Kopfzeile springt beim
              Umschalten nicht in der Hoehe. */}
          <div className="text-right flex-shrink-0">
            <div className="text-4xl font-bold text-primary-500 leading-none">
              {mitWertung ? team.total_points ?? 0 : erledigt}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {mitWertung ? "Punkte" : `von ${gesamt} Spielen`}
            </div>
          </div>
        </div>

        {mitWertung && team.age_bonus > 0 && (
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
            <Trophy className="w-3 h-3 text-primary-500" />
            Davon {team.age_bonus} Punkte Altersbonus
          </p>
        )}

        {/* Fortschritt */}
        <div className="mt-4">
          <div className="w-full bg-secondary-800 rounded-full h-3">
            <div
              className="h-3 rounded-full bg-primary-500 transition-all"
              style={{ width: `${prozent}%` }}
            />
          </div>
          <p className="text-sm mt-1 text-gray-400">
            {erledigt} / {gesamt} Stationen absolviert
          </p>
          <div className="flex gap-2 mt-3">
            <span className="flex items-center gap-1 text-sm font-semibold px-3 py-1 rounded-full bg-green-500/20 text-green-300 border border-green-500/40">
              <CheckCircle2 aria-hidden="true" className="w-4 h-4" />
              {erledigt} erledigt
            </span>
            <span className="flex items-center gap-1 text-sm font-semibold px-3 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40">
              <Circle aria-hidden="true" className="w-4 h-4" />
              {Math.max(gesamt - erledigt, 0)} offen
            </span>
          </div>
        </div>
      </div>

      {/* Hinweis auf die Platzierung – das haeufigste Missverstaendnis dieser
          Seite ist die Erwartung, hier den eigenen Rang zu sehen. */}
      <p className="text-xs text-gray-500 text-center mb-4 px-2">
        {mitWertung
          ? "Hier siehst du nur die Ergebnisse deines eigenen Teams. Die Platzierung wird bei der Preisverleihung bekanntgegeben."
          : "Hier siehst du eure eingetragenen Ergebnisse. Punkte und Platzierung werden erst nach dem Ende der Spiele bekanntgegeben."}
        {" "}Tippe auf ein Spiel, um die Beschreibung und die Regeln zu lesen.
      </p>

      {/* Stationen */}
      <ul className="space-y-2">
        {stationen.map((s) => (
          <StationZeile
            key={s.station_number}
            s={s}
            mitWertung={mitWertung}
            markiert={markiert === s.station_number}
            beiAufklappen={beiAufklappen}
          />
        ))}
      </ul>

      <LageplanKarte
        stationen={stationen}
        orte={daten.lageplan_orte || []}
        onStation={zuStation}
        aufgeklappt={aufgeklappt}
      />

      {/* Fusszeile */}
      <div className="flex items-center justify-between mt-6 text-xs text-gray-500">
        <span>Aktualisiert: {aktualisiert || "-"}</span>
        <button
          onClick={() => laden()}
          className="flex items-center gap-1 px-2 py-1 rounded hover:bg-secondary-800 text-gray-400"
        >
          <RefreshCw className="w-3 h-3" />
          Jetzt aktualisieren
        </button>
      </div>

      {daten.results_published && (
        <p className="text-center mt-4">
          <a href="/ergebnisse" className="text-primary-500 text-sm underline">
            Zur Gesamtwertung
          </a>
        </p>
      )}
    </div>
  );
}

/**
 * Eine Station der Liste. Hat sie eine Spielbeschreibung oder Regeln, klappt ein
 * Tipp auf die Zeile beides auf – so lesen Teams direkt am Stand nach, wie das
 * Spiel geht. Technische Angaben (Einheit, Punktelogik, Material …) kommen vom
 * Backend gar nicht erst mit und tauchen hier deshalb nicht auf.
 *
 * Der Auf-/Zu-Zustand ist je Zeile lokal: das 45-s-Nachladen ersetzt nur die
 * Daten, die Zeilen (Schluessel = Stationsnummer) bleiben dabei offen.
 */
function StationZeile({ s, mitWertung, markiert, beiAufklappen }) {
  const [offen, setOffen] = useState(false);
  const beschreibung = (s.game_description || "").trim();
  const regeln = (s.rules || "").trim();
  const hatDetails = !!(beschreibung || regeln);
  const detailId = `station-${s.station_number}-details`;

  useEffect(() => {
    beiAufklappen(s.station_number, offen);
  }, [offen, s.station_number, beiAufklappen]);

  // Erledigt/offen muss auf einen Blick erkennbar sein (auch im Sonnenlicht und
  // fuer Farbschwache): Symbol + Textplakette + Farbe + Rahmenstil, nie nur Farbe.
  const kopf = (
    <>
      {s.erledigt ? (
        <CheckCircle2 aria-hidden="true" className="w-8 h-8 flex-shrink-0 mr-3 text-green-400" />
      ) : (
        <Circle aria-hidden="true" className="w-8 h-8 flex-shrink-0 mr-3 text-amber-400" />
      )}

      <div className="min-w-0 flex-1">
        <div
          className={`text-sm font-medium truncate ${
            s.erledigt ? "text-white" : "text-gray-200"
          }`}
        >
          {s.station_name || `Station ${s.station_number}`}
        </div>
        <div className="text-xs text-gray-500">Station #{s.station_number}</div>
      </div>

      <div className="text-right flex-shrink-0 ml-3">
        <span
          className={`inline-block text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
            s.erledigt
              ? "bg-green-500/20 text-green-300 border border-green-500/40"
              : "bg-amber-500/15 text-amber-300 border border-amber-500/40"
          }`}
        >
          {s.erledigt ? "Erledigt" : "Offen"}
        </span>
        {s.erledigt && (
          <>
            {/* Ohne Wertung traegt der Rohwert die Zeile und wird deshalb
                hervorgehoben; mit Wertung ruecken die Punkte nach vorne
                und der Rohwert darunter. */}
            {mitWertung && (
              <div className="font-semibold text-primary-500 mt-1">{s.points ?? 0} Pkt.</div>
            )}
            <div
              className={
                mitWertung ? "text-xs text-gray-400" : "text-sm font-semibold text-white mt-1"
              }
            >
              {s.station_type === "estimate" ? (
                <>Schätzung: {s.estimate_value ?? "-"} {s.unit}</>
              ) : (
                <>Ergebnis: {s.performance_score ?? "-"} {s.unit}</>
              )}
            </div>
          </>
        )}
      </div>

      {hatDetails && (
        <ChevronDown
          aria-hidden="true"
          className={`w-4 h-4 ml-2 flex-shrink-0 text-gray-400 transition-transform ${
            offen ? "rotate-180" : ""
          }`}
        />
      )}
    </>
  );

  return (
    <li
      id={`station-${s.station_number}-zeile`}
      className={`rounded border border-l-4 transition-shadow ${
        markiert ? "ring-2 ring-primary-500 " : ""
      }${
        s.erledigt
          ? "bg-green-500/10 border-green-500/40 border-l-green-500"
          : "bg-secondary-900/50 border-dashed border-amber-500/40 border-l-amber-500"
      }`}
    >
      {hatDetails ? (
        <button
          type="button"
          onClick={() => setOffen((o) => !o)}
          aria-expanded={offen}
          aria-controls={detailId}
          className="w-full flex items-center justify-between p-3 text-left"
        >
          {kopf}
        </button>
      ) : (
        <div className="flex items-center justify-between p-3">{kopf}</div>
      )}

      {hatDetails && offen && (
        <div
          id={detailId}
          className="px-3 pb-3 pt-2 space-y-3 border-t border-secondary-800 text-sm"
        >
          {beschreibung && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">
                So geht’s
              </h3>
              <p className="text-gray-200 whitespace-pre-line break-words">{beschreibung}</p>
            </section>
          )}
          {regeln && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">
                Regeln
              </h3>
              <p className="text-gray-200 whitespace-pre-line break-words">{regeln}</p>
            </section>
          )}
        </div>
      )}
    </li>
  );
}

// Pin-Groessen wie im Pin-Editor (pages/Lageplan.jsx): w-5 / w-7 / w-9.
const PIN_GROESSEN = {
  klein: { kreis: "w-5 h-5", text: "text-[9px]" },
  mittel: { kreis: "w-7 h-7", text: "text-[11px]" },
  gross: { kreis: "w-9 h-9", text: "text-sm" },
};

const hatPin = (e) => e.map_x != null && e.map_y != null;

/**
 * Ausklappbarer Lageplan unter der Stationsliste.
 *
 * Das Bild wird erst beim Aufklappen geladen – die meisten Teams brauchen den
 * Plan nur einmal, und auf dem Festgelaende zaehlt jedes Kilobyte. Die Pins der
 * Stationen folgen dem Status der Liste (gruen = erledigt, gelb = offen), damit
 * man auf einen Blick sieht, wohin es als Naechstes geht. Eigene Orte stehen als
 * blaue Quadrate mit Buchstaben darauf und sind darunter erklaert, wie auf dem
 * gedruckten Team-Zettel.
 *
 * Gibt es weder Pins noch Orte, erscheint der Bereich gar nicht.
 */
function LageplanKarte({ stationen, orte, onStation, aufgeklappt }) {
  const [offen, setOffen] = useState(false);
  const [planFehlt, setPlanFehlt] = useState(false);

  const stationsPins = stationen.filter(hatPin);
  const ortsPins = orte.filter(hatPin);
  // Ist ein Spiel aufgeklappt, treten die anderen Pins etwas zurueck.
  const fokus = aufgeklappt.size > 0;
  if (stationsPins.length === 0 && ortsPins.length === 0) return null;

  return (
    <section className="mt-6 rounded border border-secondary-800 bg-secondary-900/50">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        aria-controls="lageplan-inhalt"
        className="w-full flex items-center justify-between p-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-gray-200">
          <MapPin aria-hidden="true" className="w-5 h-5 text-primary-500" />
          Lageplan
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`w-4 h-4 text-gray-400 transition-transform ${offen ? "rotate-180" : ""}`}
        />
      </button>

      {offen && (
        <div id="lageplan-inhalt" className="px-3 pb-3 pt-2 border-t border-secondary-800">
          {planFehlt ? (
            <p className="text-sm text-gray-400">Der Lageplan ist gerade nicht verfügbar.</p>
          ) : (
            <>
              <div className="relative inline-block max-w-full">
                <img
                  src={`${BACKEND_URL}/public/lageplan`}
                  alt="Lageplan des Festgeländes"
                  onError={() => setPlanFehlt(true)}
                  className="max-w-full h-auto rounded select-none"
                  draggable={false}
                />

                {stationsPins.map((s) => {
                  const { kreis, text } = PIN_GROESSEN[s.map_size] || PIN_GROESSEN.mittel;
                  return (
                    <button
                      type="button"
                      onClick={() => onStation(s.station_number)}
                      aria-label={`Zu Station ${s.station_number} in der Liste springen`}
                      key={s.station_number}
                      title={s.station_name || `Station ${s.station_number}`}
                      style={{ left: `${s.map_x}%`, top: `${s.map_y}%` }}
                      className={`absolute -translate-x-1/2 -translate-y-1/2 ${kreis} rounded-full flex items-center justify-center ${text} font-bold border cursor-pointer transition-all ${
                        aufgeklappt.has(s.station_number)
                          ? "z-10 scale-125 ring-2 ring-white shadow-lg"
                          : fokus
                          ? "opacity-50"
                          : ""
                      } ${
                        s.erledigt
                          ? "bg-green-400 text-black border-black/70"
                          : "bg-primary-500 text-black border-black/70"
                      }`}
                    >
                      {s.station_number}
                    </button>
                  );
                })}

                {ortsPins.map((o) => {
                  const { kreis, text } = PIN_GROESSEN[o.map_size] || PIN_GROESSEN.mittel;
                  return (
                    <span
                      key={o.buchstabe}
                      title={o.name}
                      style={{ left: `${o.map_x}%`, top: `${o.map_y}%` }}
                      className={`absolute -translate-x-1/2 -translate-y-1/2 ${kreis} rounded-md flex items-center justify-center ${text} font-bold border-2 bg-[#2563EB] text-white border-white`}
                    >
                      {o.buchstabe}
                    </span>
                  );
                })}
              </div>

              <div className="mt-3 space-y-1 text-xs text-gray-400">
                <p>Zahlen sind die Spiele (grün = erledigt, gelb = offen). Tippe auf eine Zahl, um zum Spiel in der Liste zu springen.</p>
                {ortsPins.length > 0 && (
                  <ul className="flex flex-wrap gap-x-4 gap-y-1">
                    {ortsPins.map((o) => (
                      <li key={o.buchstabe} className="flex items-center gap-1">
                        <span className="w-4 h-4 rounded bg-[#2563EB] text-white text-[10px] font-bold flex items-center justify-center">
                          {o.buchstabe}
                        </span>
                        {o.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function Hinweis({ farbe, children }) {
  const stile = {
    yellow: "border-yellow-500/30 bg-yellow-500/10 text-yellow-200",
    red: "border-red-500/30 bg-red-500/10 text-red-200",
  };
  return (
    <div className="p-6 text-center">
      <div className={`rounded-lg border p-4 inline-block max-w-md ${stile[farbe]}`}>
        {children}
      </div>
    </div>
  );
}
